import React from "react";
import { Box, Button, Container, Paper, Typography } from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { PRESETS } from "./datePresets";

const ACCENT = "#26890d";

// Quick-action subset of the header's date presets — same source of truth, same keys.
const QUICK_PRESET_KEYS = ["7d", "30d", "90d"];
const quickPresets = PRESETS.filter((p) => QUICK_PRESET_KEYS.includes(p.key));

const EmptyState = ({ fromDate, toDate, onPresetClick }) => {
  return (
    <Container maxWidth="md">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          py: 4,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            borderRadius: "10px",
            border: "1px solid #e8e8e8",
            backgroundColor: "#fff",
            padding: "40px 32px",
            textAlign: "center",
            width: "100%",
            transition: "box-shadow 0.2s",
            "&:hover": {
              boxShadow: "0 10px 24px rgba(0, 0, 0, 0.08)",
            },
          }}
        >
          {/* Icon */}
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "10px",
              backgroundColor: "#f0f8e0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <CalendarTodayIcon sx={{ fontSize: 24, color: ACCENT }} />
          </Box>

          {/* Message */}
          <Typography sx={{ fontSize: "1.05rem", fontWeight: 700, color: "#1a1a1a", mb: 0.5 }}>
            No usage data yet
          </Typography>

          <Typography sx={{ fontSize: "0.85rem", color: "#666", mb: 1.5, lineHeight: 1.5 }}>
            No Copilot activity was recorded for the selected date range.
          </Typography>

          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.6,
              px: 1.2,
              py: 0.35,
              backgroundColor: "#f7f7f7",
              border: "1px solid #e8e8e8",
              borderRadius: "20px",
              mb: 3,
            }}
          >
            <CalendarTodayIcon sx={{ fontSize: "12px", color: "#777" }} />
            <Typography sx={{ fontSize: "12px", color: "#777", fontWeight: 700 }}>
              {fromDate} &rarr; {toDate}
            </Typography>
          </Box>

          {/* Suggestions */}
          <Typography
            sx={{
              fontSize: "0.72rem",
              color: "#999",
              fontWeight: 700,
              mb: 1.2,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Try a wider range
          </Typography>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center" }}>
            {quickPresets.map((preset) => (
              <Button
                key={preset.key}
                variant="outlined"
                size="small"
                onClick={() => onPresetClick(preset)}
                sx={{
                  borderRadius: "16px",
                  borderColor: "#ccc",
                  color: "#555",
                  fontWeight: 500,
                  fontSize: "0.8rem",
                  textTransform: "none",
                  px: 1.5,
                  "&:hover": {
                    backgroundColor: ACCENT,
                    color: "#fff",
                    borderColor: ACCENT,
                  },
                }}
              >
                {preset.label}
              </Button>
            ))}
          </Box>

          {/* Help text */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, mt: 2.5 }}>
            <InfoOutlinedIcon sx={{ fontSize: "0.85rem", color: "#bbb" }} />
            <Typography sx={{ fontSize: "0.72rem", color: "#999" }}>
              Data may take 24 hours to appear after Copilot usage.
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default EmptyState;
